// Controlli dopo la cottura su un lotto (mantenimento a caldo, esposizione
// temporanea, verifiche della procedura). Registro facoltativo: nessun campo
// obbligatorio oltre al valore misurato; un esito non conforme propone, senza
// imporla, l'apertura di una non conformità sul lotto (modulo esistente).
import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, AlertTriangle, CheckCircle2 } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { formatItalianDateTime } from "../lib/dateUtils";
import { Card, Button, Field, Input } from "./ui-kit";
import { POST_COOKING_LABELS } from "../lib/postCookingModes";

const KIND_LABELS = { hot_start: "Inizio mantenimento a caldo", temperature: "Temperatura di mantenimento", display_start: "Inizio esposizione", display_end: "Fine esposizione", check: "Verifica della procedura" };

// Quali controlli proporre per ogni modalità (procedura personalizzata: tutti).
export const POST_COOKING_CHECK_KINDS = {
  hot_holding: ["hot_start", "temperature", "check"],
  temporary_display: ["display_start", "display_end", "check"],
  ambient: ["check"],
  custom: ["hot_start", "temperature", "display_start", "display_end", "check"],
};

const STATUS_STYLE = { conforme: "bg-emerald-50 text-emerald-700", non_conforme: "bg-red-50 text-red-700" };

export default function PostCookingChecksDialog({ production, onClose }) {
  const [data, setData] = useState(null);
  const kinds = POST_COOKING_CHECK_KINDS[production.post_cooking_destination] || ["check"];
  const [kind, setKind] = useState(kinds[0]);
  const [value, setValue] = useState("");
  const [conforme, setConforme] = useState(true);
  const [note, setNote] = useState("");
  const [endDestination, setEndDestination] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastNc, setLastNc] = useState(null);
  const load = () => api.get(`/app/productions/${production.id}/post-cooking-checks`).then(({ data: d }) => setData(d)).catch(() => setData({ checks: [] }));
  useEffect(() => { load(); }, [production.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const process = data?.post_cooking_process || {};

  const save = async () => {
    if (kind === "temperature" && value === "") { toast.error("Indica la temperatura misurata"); return; }
    setBusy(true);
    try {
      const { data: d } = await api.post(`/app/productions/${production.id}/post-cooking-checks`, {
        kind, value: kind === "temperature" ? parseFloat(value) : null, conforme: kind === "check" ? conforme : null,
        note, end_destination: kind === "display_end" ? endDestination : "",
      });
      toast.success("Controllo registrato");
      setLastNc(d.check.status === "non_conforme" ? d.check : null);
      setValue(""); setNote(""); setEndDestination("");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openNc = async () => {
    const tooLong = lastNc.max_hours != null && lastNc.elapsed_hours > lastNc.max_hours;
    const detail = lastNc.kind === "temperature" ? [lastNc.limit_min != null && lastNc.value < lastNc.limit_min && `temperatura ${lastNc.value} °C sotto il minimo di ${lastNc.limit_min} °C`, tooLong && `mantenimento a caldo da ${lastNc.elapsed_hours} ore oltre il massimo di ${lastNc.max_hours} ore`].filter(Boolean).join("; ")
      : lastNc.kind === "display_end" ? `esposizione di ${lastNc.elapsed_hours} ore oltre il massimo di ${lastNc.max_hours} ore` : "verifica non conforme";
    try {
      await api.post("/app/nonconformities", {
        site_id: production.site_id, production_id: production.id, severity: "media",
        cause: `${POST_COOKING_LABELS[production.post_cooking_destination] || "Dopo la cottura"} — lotto ${production.lot_code}: ${detail}${lastNc.note ? ` (${lastNc.note})` : ""}`,
        corrective_action: process.corrective_action || "",
      });
      toast.success("Non conformità aperta: completala in Non conformità");
      setLastNc(null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-end sm:place-items-start p-0 sm:p-6" data-testid="post-cooking-checks-dialog">
      <div className="absolute inset-0 bg-navy/50" onClick={busy ? undefined : onClose} />
      <Card className="relative w-full sm:max-w-xl max-h-[92vh] overflow-y-auto p-6 rounded-b-none sm:rounded-xl">
        <div className="mb-1 flex items-start justify-between gap-2">
          <h3 className="font-heading text-xl font-semibold">Controlli dopo la cottura</h3>
          <button onClick={onClose} disabled={busy} className="p-2 text-slate-400 hover:text-navy" aria-label="Chiudi"><X className="h-5 w-5" /></button>
        </div>
        <p className="text-sm text-slate-500">{production.recipe_name?.toUpperCase()} · lotto <span className="font-mono">{production.lot_code}</span> · {POST_COOKING_LABELS[production.post_cooking_destination]}</p>
        {(process.holding_temp_c != null || process.max_holding_hours != null || process.max_display_hours != null) && (
          <p className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50 p-3 text-xs text-indigo-950">
            Limiti della procedura: {[process.holding_temp_c != null && `mantenimento ≥ ${process.holding_temp_c} °C`, process.max_holding_hours != null && `massimo ${process.max_holding_hours} ore a caldo`, process.max_display_hours != null && `esposizione massima ${process.max_display_hours} ore`].filter(Boolean).join(" · ")}
          </p>
        )}
        <p className="mt-2 text-xs text-slate-500">Registrazione facoltativa, secondo la frequenza prevista dalla procedura approvata.</p>

        <div className="mt-4 grid gap-2 sm:grid-cols-2">
          {kinds.map((k) => (
            <button key={k} type="button" onClick={() => setKind(k)} aria-pressed={kind === k} className={`min-h-[44px] rounded-lg border-2 px-3 text-left text-sm ${kind === k ? "border-indigo-500 bg-indigo-50 font-semibold text-indigo-900" : "border-hairline bg-white text-navy"}`} data-testid={`post-cooking-kind-${k}`}>{KIND_LABELS[k]}</button>
          ))}
        </div>
        <div className="mt-3 grid gap-3">
          {kind === "temperature" && <Field label="Temperatura misurata (°C) *"><Input type="number" step="0.1" value={value} onChange={(e) => setValue(e.target.value)} data-testid="post-cooking-value" /></Field>}
          {kind === "check" && (
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setConforme(true)} aria-pressed={conforme} className={`min-h-[44px] rounded-lg border-2 text-sm ${conforme ? "border-emerald-500 bg-emerald-50 font-semibold text-emerald-800" : "border-hairline"}`}>Conforme</button>
              <button type="button" onClick={() => setConforme(false)} aria-pressed={!conforme} className={`min-h-[44px] rounded-lg border-2 text-sm ${!conforme ? "border-red-500 bg-red-50 font-semibold text-red-800" : "border-hairline"}`}>Non conforme</button>
            </div>
          )}
          {kind === "display_end" && <Field label="Destinazione del prodotto non venduto"><Input value={endDestination} onChange={(e) => setEndDestination(e.target.value)} placeholder={process.end_destination || "Es. eliminato"} /></Field>}
          <Field label="Note"><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Facoltative" /></Field>
          <Button variant="primary" loading={busy} onClick={save} data-testid="post-cooking-save">Registra</Button>
        </div>

        {lastNc && (
          <div className="mt-3 rounded-xl border-2 border-red-300 bg-red-50 p-3 text-sm text-red-800" role="alert">
            <div className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Esito non conforme</div>
            <p className="mt-1 text-xs">Applica l’azione correttiva prevista{process.corrective_action ? `: ${process.corrective_action}` : ""}. Vuoi registrare una non conformità sul lotto?</p>
            <div className="mt-2 flex gap-2">
              <Button variant="danger" onClick={openNc} data-testid="post-cooking-open-nc">Apri non conformità</Button>
              <Button variant="ghost" onClick={() => setLastNc(null)}>Non ora</Button>
            </div>
          </div>
        )}

        <div className="mt-5 text-xs font-semibold uppercase tracking-wider text-slate-400">Registrazioni del lotto</div>
        <ul className="mt-2 divide-y divide-hairline rounded-lg border border-hairline" data-testid="post-cooking-history">
          {(data?.checks || []).map((c) => (
            <li key={c.id} className="flex items-start justify-between gap-3 p-3 text-sm">
              <div className="min-w-0">
                <div className="font-medium text-navy">{KIND_LABELS[c.kind]}{c.value != null ? ` · ${c.value} °C` : ""}{c.elapsed_hours != null ? ` · dopo ${c.elapsed_hours} ore` : ""}</div>
                <div className="text-xs text-slate-500">{formatItalianDateTime(c.at)} · {c.operator_name || "—"}{c.end_destination ? ` · ${c.end_destination}` : ""}{c.note ? ` · ${c.note}` : ""}</div>
              </div>
              {c.status && <span className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[c.status]}`}>{c.status === "conforme" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{c.status === "conforme" ? "Conforme" : "Non conforme"}</span>}
            </li>
          ))}
          {data && !data.checks.length && <li className="p-3 text-sm text-slate-400">Nessun controllo registrato.</li>}
        </ul>
      </Card>
    </div>
  );
}
